import { useEffect, useRef, useState } from "react";
import { useMotionPolicy } from "../lib/hooks/useMotionPolicy";
import { usePresentedSnapshot } from "../presentation/hooks";
import { useNexusStore } from "../store/nexusStore";
import { NexusEngine } from "./core/NexusEngine";

export function NexusSceneHost({ displayRefreshHz }: { displayRefreshHz: number }) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const engineRef = useRef<NexusEngine | null>(null);
  const snapshot = usePresentedSnapshot();
  const focus = useNexusStore((state) => state.focus);
  const hoveredEntityId = useNexusStore((state) => state.hoveredEntityId);
  const activePulse = useNexusStore((state) => state.activePulse);
  const activateNode = useNexusStore((state) => state.activateNode);
  const focusOrbital = useNexusStore((state) => state.focusOrbital);
  const hoverEntity = useNexusStore((state) => state.hoverEntity);
  const motion = useMotionPolicy();
  const idleMode = useNexusStore((state) => state.idleMode);
  const accessibleMode = useNexusStore((state) => state.accessibleMode);
  const demoTourActive = useNexusStore((state) => state.demoTourActive);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const controller = new AbortController();
    const engine = new NexusEngine(host, {
      onNodeActivate: activateNode,
      onOrbitalActivate: focusOrbital,
      onHover: hoverEntity,
    });
    engineRef.current = engine;
    void engine.init(controller.signal).catch((reason: unknown) => {
      if (controller.signal.aborted) return;
      console.error("Unified WebGPU scene initialization failed", reason);
      setError(reason instanceof Error ? reason.message : "Unified WebGPU scene could not be initialized.");
    });
    return () => {
      controller.abort();
      engine.dispose();
      engineRef.current = null;
    };
  }, [activateNode, focusOrbital, hoverEntity]);

  useEffect(() => engineRef.current?.setSnapshot(snapshot), [snapshot]);
  useEffect(() => engineRef.current?.setFocus(focus), [focus]);
  useEffect(() => engineRef.current?.setHovered(hoveredEntityId), [hoveredEntityId]);
  useEffect(() => engineRef.current?.setPulse(activePulse), [activePulse]);
  useEffect(() => engineRef.current?.setReducedMotion(motion.reduced), [motion.reduced]);
  useEffect(() => engineRef.current?.setDisplayRefreshHz(displayRefreshHz), [displayRefreshHz]);
  useEffect(() => engineRef.current?.setIdleMode(idleMode), [idleMode]);
  useEffect(() => engineRef.current?.setAccessibleMode(accessibleMode), [accessibleMode]);
  useEffect(() => engineRef.current?.setDemoTourActive(demoTourActive), [demoTourActive]);

  return (
    <div ref={hostRef} className="nexus-engine-host" data-engine="webgpu-unified">
      {error && (
        <div className="engine-error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}
